import { useAuth } from "wasp/client/auth";
import { SubscriptionStatus, planHasEdgyAccess } from "../payment/plans";

/**
 * Client-side view of the Edgy beta entitlement.
 *
 * Edgy is Max-only while it is in beta. The authoritative check lives on the
 * server (`isEdgyBetaLocked`, driven by EDGY_BETA_MAX_ONLY) — this hook only
 * decides what the UI paints, so the padlock can render on first frame without
 * waiting for a round trip.
 *
 * Two consequences of the flag being server-side only:
 * - The optimistic answer here assumes the beta is ON. That matches the server
 *   default, and erring towards "locked" shows an upgrade screen rather than
 *   letting a Pro user start a flow the stream would refuse.
 * - `getEdgyCreditState.betaLocked` is the real answer. Pass it as `serverState`
 *   wherever it has already been fetched (the drawer does) and it wins, so the
 *   day the beta is lifted the UI unlocks without a redeploy.
 */
export function useEdgyAccess(
  // `Record<string, unknown>` rather than `{ betaLocked?: boolean }`: a type whose
  // members are all optional is a "weak type", and TS rejects any argument sharing
  // no property with it. EdgyCreditStateDto's index signature (required by Wasp's
  // SuperJSON Payload constraint) trips exactly that rule, so the field is read
  // dynamically and narrowed below instead.
  serverState?: Record<string, unknown> | null,
): {
  /** True when the padlock and the upgrade screen must be shown. */
  isLocked: boolean;
} {
  const { data: user } = useAuth();

  // The server has spoken — trust it over the local guess.
  if (serverState && typeof serverState.betaLocked === "boolean") {
    return { isLocked: serverState.betaLocked };
  }

  // Logged out: nothing to unlock, and the button is not rendered in-app anyway.
  if (!user) return { isLocked: true };

  // A deleted subscription is no longer a Max entitlement, mirroring how the
  // broker-sync gates treat it.
  if (user.subscriptionStatus === SubscriptionStatus.Deleted) return { isLocked: true };

  return { isLocked: !planHasEdgyAccess(user.subscriptionPlan) };
}
